import { Injectable, Inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { IState, InitialState, Modes } from '../models/component-state.model';
import { Value } from '../models/abstract-value-accessor.model';
import { DOCUMENT } from '@angular/common';

/** @dynamic */
@Injectable({
  providedIn: 'root'
})
export class DfMonthpickerService {
  private state$: BehaviorSubject<IState>;
  private today: Date;

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.today = new Date();
    this.state$ = new BehaviorSubject({
      ...InitialState,
      selected: { year: this.getToday().year, month: 1 }
    });
  }

  isMobileScreen(): boolean {
    return this.document.documentElement.clientWidth <= 576;
  }

  getState(): Observable<IState> {
    return this.state$.asObservable();
  }

  getLastState(): IState {
    return { ...this.state$.value };
  }

  getToday = (): Value => ({
    year: this.today.getFullYear(),
    month: this.today.getMonth()
  });

  update(data: Partial<IState>) {
    this.state$.next({ ...this.state$.value, ...data });
  }

  /** starting year for current range */
  getBaseYear(): number {
    return +(
      this.state$.value.selected && this.state$.value.selected.year - (this.state$.value.selected.year % 10) - 1
    );
  }

  /**
   * Get value at index, related to current range.
   * Main value part of current mode (month or year) is calculated for specified index,
   * secondary value part is taken from latest selected value
   * */
  getValueAtIndex(index: number): Value {
    let baseYear = this.getBaseYear();
    if (this.state$.value.mode === Modes.year) {
      baseYear += index < 0 ? -10 : index > baseYear + 10 ? 10 : 0;
      index += index > 0 ? 0 : 10;

      return {
        year: index + baseYear,
        month: this.state$.value.selected.month
      };
    }
    const yearOverflow = index > 11 ? 1 : index < 0 ? -1 : 0;
    index += index < 0 ? 12 : index > 11 ? -12 : 0;
    return {
      year: this.state$.value.selected.year + yearOverflow,
      month: index + 1
    };
  }

  getSelectedIndex(): number {
    if (this.state$.value.mode === Modes.year) {
      return this.state$.value.selected.year - this.getBaseYear();
    } else {
      return this.state$.value.selected.month - 1;
    }
  }

  isTodayYear = (index: number) => index + this.getBaseYear() === this.today.getFullYear();
  isTodayMonth = (index: number) =>
    this.today.getMonth() === index && this.state$.value.selected.year === this.today.getFullYear();
  isSelectedYear = (index: number) => index + this.getBaseYear() === this.state$.value.selected.year;
  isSelectedMonth = (index: number) => index + 1 === this.state$.value.selected.month;

  getSeparator(): string {
    return this.state$.value.mask.replace(/m|y/gi, '');
  }

  getIsMonthFirst(): boolean {
    return this.state$.value.mask.indexOf('y') > 0;
  }

  compareValues(v1: Value, v2: Value): number {
    if (!v1 || !v2) {
      return !v1 && !v2 ? 0 : null;
    }
    return Math.sign((v1.year || 0) - (v2.year || 0)) || Math.sign((v1.month || 0) - (v2.month || 0));
  }

  /**
   * Value string may be used in comparison with user input,
   * therefore padding must be applied separately from formatting
   */
  padMonthWithZero(valueString: string): string {
    const monthStart = this.getIsMonthFirst() ? 0 : valueString.indexOf(this.getSeparator()) + 1;
    return valueString.substr(0, monthStart) + '0' + valueString.substr(monthStart);
  }

  formatValue(value: Value): string {
    if (!value) {
      return '';
    }
    const valueParts = [value.month, value.year];
    if (!this.getIsMonthFirst()) {
      valueParts.reverse();
    }
    if (!valueParts[0]) {
      return '';
    }
    // value with both month and year
    if (valueParts[1] !== null) {
      return valueParts.map(v => (v ? v.toString() : '')).join(this.getSeparator());
    }
    // value without month or year
    return valueParts[0].toString();
  }

  formatValueString(value: string): Value {
    const parts = value.split(this.getSeparator()).map((v: string) => (v && v.replace(/[^0-9]+/g, '')) || null);
    const monthFirst = this.getIsMonthFirst();
    let monthString = (monthFirst ? parts[0] : parts[1]) || null;
    let yearString = (monthFirst ? parts[1] : parts[0]) || null;
    if (monthFirst && monthString && monthString.length > 2 && !yearString) {
      yearString = monthString.slice(2);
    }
    if (!monthFirst && yearString && yearString.length > 4 && !monthString) {
      monthString = yearString.slice(4);
    }
    return {
      year: yearString && +yearString.slice(0, 4),
      month: monthString && +monthString.slice(0, 2)
    };
  }
}
